import { Collection, DynamicLoader, HorizontalAlignment, StringUtil } from "@mcleod/core";
import { Component, ComponentConstructor, Label, Layout, LayoutProps, Panel } from "@mcleod/components";
import { AbstractComponentDemoExamples } from "./examples/AbstractComponentDemoExamples";
import { AddlExampleSectionPanel } from "./AddlExampleSectionPanel";
import { ComponentDemoDefinition } from "./ComponentDemoDefinition";
import { ComponentPropertySorter } from "./ComponentPropertySorter";
import { DemoRegistry } from "../DemoRegistry";
import { ExamplesConstructor } from "./ExamplesConstructor";
import { PropertySectionPanel } from "./PropertySectionPanel";
import { LiveDemoSectionPanel } from "./LiveDemoSectionPanel";

/**
 * Having this interface allows us to easily pass the demoDefinition id from nested demo router in as a normal property.
 */
export interface ComponentDemoProps extends LayoutProps {
    demoDefinition: ComponentDemoDefinition|string;
}

/**
 * ComponentDemo is a wrapper around Layout that can programmatically build a demo page for a component.
 */
export class ComponentDemo extends Layout implements ComponentDemoProps {
    private _demoDefinition: ComponentDemoDefinition;
    private componentClassRef: ComponentConstructor;
    private panelContent: Panel;
    
    constructor(props: Partial<ComponentDemoProps>) {
        super({
            fillRow: true,
            align: HorizontalAlignment.CENTER,
            needsServerLayout: false,
            ...props
        });
        this.demoDefinition = props.demoDefinition;
        this.id = `ComponentDemo-${this.demoDefinition.componentName}`,
        this.addPanelContent();
        this.addTitleLabel();
        this.loadComponentClassRef();
        const propertySorter = new ComponentPropertySorter(this.createComponentInstance());
        this.createSections(propertySorter);
    }

    get demoDefinition(): ComponentDemoDefinition {
        return this._demoDefinition;
    }

    set demoDefinition(value: ComponentDemoDefinition|string) {
        if (value instanceof ComponentDemoDefinition)
            this._demoDefinition = value;
        else if (StringUtil.isEmptyString(value) !== true)
            this._demoDefinition = DemoRegistry.get().getObject(value) as ComponentDemoDefinition;
    }

    private get componentName(): string {
        return this.demoDefinition.componentName;
    }

    private addPanelContent() {
        this.panelContent = new Panel({ fillRow: true });
        this.add(this.panelContent);
    }

    private addTitleLabel() {
        const titleLabel = new Label({
            text: this.componentName + " API",
            fontSize: "xxlarge",
            fontBold: true,
            imageName: this.demoDefinition.imageName })
        this.panelContent.add(titleLabel);
    }

    private loadExamplesSource(): AbstractComponentDemoExamples {
        const examplesClassRef: ExamplesConstructor = DynamicLoader.getClassForPath(this.demoDefinition.examplesClass);
        if (examplesClassRef != null)
            return new examplesClassRef();
    }

    private loadComponentClassRef() {
        this.componentClassRef = DynamicLoader.getClassForPath(this.demoDefinition.componentPath);
    }

    private createComponentInstance(): Component {
        return new this.componentClassRef();
    }

    private createSections(propertySorter: ComponentPropertySorter) {
        const examples = this.loadExamplesSource();
        this.addLiveDemoSection(examples);
        this.addLocalSection(propertySorter, examples);
        this.addDataboundSection(propertySorter, examples);
        this.addOtherSection(propertySorter, examples);
        this.addAllCompSection(propertySorter, examples);
        this.addAddlExamplesSection(examples);
    }

    private addLiveDemoSection(examples: AbstractComponentDemoExamples) {
        if (examples != null) {
            const title = "Live Demos";
            const defs: Collection<any> = examples.liveDemoDefs;
            this.addSection(LiveDemoSectionPanel, examples, defs, title);
        }
    }

    private addLocalSection(propertySorter: ComponentPropertySorter, examples: AbstractComponentDemoExamples) {
        const title = "Properties/methods defined in " + this.componentName;
        this.addSection(PropertySectionPanel, examples, propertySorter.localProps, title);
    }

    private addDataboundSection(propertySorter: ComponentPropertySorter, examples: AbstractComponentDemoExamples) {
        const title = "Properties/methods shared by all data-bound components";
        this.addSection(PropertySectionPanel, examples, propertySorter.databoundProps, title);
    }

    private addOtherSection(propertySorter: ComponentPropertySorter, examples: AbstractComponentDemoExamples) {
        const title = "Properties/methods defined externally";
        this.addSection(PropertySectionPanel, examples, propertySorter.otherProps, title);
    }

    private addAllCompSection(propertySorter: ComponentPropertySorter, examples: AbstractComponentDemoExamples) {
        const title = "Properties/methods shared by all components";
        this.addSection(PropertySectionPanel, examples, propertySorter.allCompProps, title);
    }

    private addAddlExamplesSection(examples: AbstractComponentDemoExamples) {
        if (examples != null) {
            const title = "Additional Examples";
            const defs: Collection<any> = examples.addlExampleDefs;
            this.addSection(AddlExampleSectionPanel, examples, defs, title);
        }
    }

    private addSection(sectionPanelClass: ComponentConstructor, examples: AbstractComponentDemoExamples,
        defs: Collection<any>, title: string) {
        if (defs == null || Object.keys(defs).length == 0)
            return;
        const compSection = new sectionPanelClass(title, defs, examples, this.componentClassRef, this.componentName);
        this.panelContent.add(compSection);
    }
}
